// ============================================================================
// OBT F2-A — Taslak kampanyayı geri açma (meta + sorular)
// ============================================================================
// F2 Backend Enabler `GET /campaigns/{id}` + `GET /campaigns/{id}/questions`
// uçlarını kullanır. Kampanya meta'sı `CampaignHistoryOut` biçimindedir ve
// `description` alanı İÇERMEZ (backend sözleşme sınırı); bu yüzden açıklama geri
// yüklenemez ve `descriptionUnavailable` bayrağı ile UI kullanıcıyı bilgilendirir.
//
// Yalnız DRAFT kampanya düzenlenebilir. Yayınlanmış/başka durumdaki kampanya
// `notEditable` ile işaretlenir; UI düzenlemeyi açmaz (immutable snapshot).
//
// `{data, loading, error}` deseni (react-query yok). Sorular backend sırasıyla
// (display_order) form state'ine yüklenir; seçenekler ve park filtresi korunur.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { getCampaign, getCampaignQuestions } from './campaignApi';
import { normalizeObtError } from '../obtErrors';

let loadKeyCounter = 0;

/** ISO datetime → <input type="datetime-local"> değeri (yerel, saniyesiz). */
function toLocalInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** Backend snapshot sorusunu builder form state şekline çevirir. */
function toFormQuestion(q) {
  loadKeyCounter += 1;
  const options = Array.isArray(q.options)
    ? q.options
        .slice()
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((o) => ({
          option_label: o.option_label ?? o.option_value ?? '',
          option_value: o.option_value ?? '',
        }))
    : [];
  return {
    _key: `loaded_${loadKeyCounter}`,
    question_id: q.question_id,
    question_text: q.question_text || '',
    question_type: q.question_type,
    is_required: q.is_required !== false,
    photo_required: Boolean(q.photo_required),
    options,
    // Park filtresi soru düzeyinde saklanır; builder üst düzeyde restore eder.
    _park_feature_key: q.park_feature_key || null,
    _filter_configuration: q.filter_configuration || null,
  };
}

/**
 * @param {string|null} campaignId  null ise yükleme yapılmaz (yeni kampanya modu)
 * @param {{enabled?: boolean}} options
 */
export function useCampaignDraft(campaignId, { enabled = true } = {}) {
  const [state, setState] = useState({
    loading: Boolean(campaignId) && enabled,
    error: null,
    data: null, // {basics, questions, version, status, featureKeys, featureMatch}
    notEditable: false,
    descriptionUnavailable: false,
  });
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!campaignId || !enabled) {
      setState({
        loading: false,
        error: null,
        data: null,
        notEditable: false,
        descriptionUnavailable: false,
      });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const [meta, questionsOut] = await Promise.all([
        getCampaign(campaignId, { signal: controller.signal }),
        getCampaignQuestions(campaignId, { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;

      const status = meta?.status || questionsOut?.campaign_status || '';
      const editable = status === 'draft';

      const rawQuestions = Array.isArray(questionsOut?.questions)
        ? questionsOut.questions
            .slice()
            .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        : [];
      const questions = rawQuestions.map(toFormQuestion);

      // Park filtresi: sorulardaki filter_configuration'dan geri türet (ilk dolu).
      let featureKeys = [];
      let featureMatch = 'any';
      const withFilter = questions.find(
        (q) => q._filter_configuration && Array.isArray(q._filter_configuration.feature_keys)
      );
      if (withFilter) {
        featureKeys = withFilter._filter_configuration.feature_keys.slice();
        featureMatch = withFilter._filter_configuration.match || 'any';
      } else {
        const keys = questions
          .map((q) => q._park_feature_key)
          .filter(Boolean);
        if (keys.length) featureKeys = Array.from(new Set(keys));
      }

      // Owner enabler sonrası: campaign detail `description` DÖNER. Alan yanıtta
      // hiç yoksa (beklenmedik eski backend) fallback: boş + uyarı bayrağı.
      const hasDescriptionField =
        meta != null && Object.prototype.hasOwnProperty.call(meta, 'description');
      setState({
        loading: false,
        error: null,
        notEditable: !editable,
        descriptionUnavailable: !hasDescriptionField,
        data: {
          status,
          version:
            typeof questionsOut?.campaign_version === 'number'
              ? questionsOut.campaign_version
              : null,
          basics: {
            title: meta?.title || '',
            description: meta?.description || '', // artık backend'den yüklenir
            topic: meta?.topic || '',
            responseDueAt: toLocalInput(meta?.response_due_at),
          },
          questions,
          featureKeys,
          featureMatch,
        },
      });
    } catch (error) {
      if (
        controller.signal.aborted ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
      ) {
        return;
      }
      setState({
        loading: false,
        error: normalizeObtError(error),
        data: null,
        notEditable: false,
        descriptionUnavailable: false,
      });
    }
  }, [campaignId, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { ...state, reload: load };
}
